import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SmoothScroll } from "../components/SmoothScroll";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useLang } from "../i18n/LangContext";
import { legal, LEGAL_SLUGS } from "../i18n/legal";

const PH_RE = /(\[(?:À COMPLÉTER|TO COMPLETE)[^\]]*\])/g;

// Renders text, highlighting owner placeholders so they are impossible to miss.
const Text = ({ children }) => children.split(PH_RE).map((part, i) =>
  PH_RE.test(part) || /^\[(À COMPLÉTER|TO COMPLETE)/.test(part)
    ? <mark key={i} data-testid="legal-placeholder" className="bg-transparent text-gold border-b border-dashed border-[var(--gold)] font-mono text-[0.8em] tracking-wide">{part}</mark>
    : <span key={i}>{part}</span>
);

export default function LegalPage() {
  const slug = useLocation().pathname.slice(1);
  const { lang } = useLang();
  const L = legal[lang];
  const page = L[slug];

  useEffect(() => { window.scrollTo(0, 0); window.__lenis?.scrollTo(0, { immediate: true }); }, [slug]);

  if (!page) return null;
  const other = LEGAL_SLUGS.find((s) => s !== slug);

  return (
    <div className="grain" data-testid={`legal-page-${slug}`}>
      <Seo title={page.seoTitle} description={page.seoDescription} path={`/${slug}`} />
      <SmoothScroll />
      <Header />
      <main className="px-6 md:px-12 pt-32 md:pt-40 pb-28">
        <Link to="/" data-testid="legal-back-link" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-stone hover:text-gold transition-colors duration-300 mb-10">
          <ArrowLeft size={14} /> {L.labels.back}
        </Link>
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight" data-testid="legal-title">
              {page.title} <em className="text-gold italic font-normal">{page.title2}</em>
            </h1>
            <p className="mt-8 font-mono text-[11px] tracking-[0.22em] uppercase text-stone">{L.labels.updated} — {page.updated}</p>
            <p className="mt-6 text-stone text-sm leading-relaxed max-w-xs border-l border-[var(--gold)] pl-4" data-testid="legal-notice">{L.labels.notice}</p>
            <p className="mt-10 font-mono text-[11px] tracking-[0.22em] uppercase text-stone">{L.labels.other}</p>
            <Link to={`/${other}`} data-testid={`legal-link-${other}`} className="mt-2 inline-block link-underline text-[var(--bone)] hover:text-gold transition-colors duration-300">
              {L[other].title} {L[other].title2}
            </Link>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            {page.sections.map((s, i) => (
              <Reveal key={s.h} delay={Math.min(i * 0.04, 0.2)} className="py-10 border-t border-line" >
                <section data-testid={`legal-section-${i}`}>
                  <h2 className="font-display text-3xl md:text-4xl">{s.h}</h2>
                  {s.p.map((p, j) => <p key={j} className="mt-5 text-stone leading-relaxed max-w-2xl"><Text>{p}</Text></p>)}
                </section>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
